import React, { useEffect, useState } from "react";
import { css } from "@emotion/css";
import type { HintEntry } from "../../types/state";
import { MathMarkdown } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";

/**
 * Step-anchored, collapsible hint affordance (the "Option 1" design).
 *
 * Hints belong to the exercise in front of you, so they live here — under
 * the activity, NOT in the chat thread.  Key behaviours:
 *
 *   • Reviewing is free + idempotent: expand/collapse and step through
 *     revealed hints (‹ prev / next ›) without ever generating one.
 *   • Generating is explicit: a separate "Reveal another hint" button
 *     (the next nudge sits locked until you ask), capped so the coach
 *     never just hands over the answer.
 *   • One hint shown at a time — progressive, never a wall of text.
 *
 * Local UI state (collapsed / which hint is in view) resets when the
 * learner moves to a new step.
 */

const DEFAULT_MAX_HINTS = 3;

const emptyButtonStyle = css`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 6px 0;
  padding: 7px 12px;
  border-radius: 9px;
  border: 1px dashed var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.85em;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-muted);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const chipStyle = css`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 6px 0;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  color: var(--color-text-muted);
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;

  &:hover { color: var(--color-text); border-color: var(--color-border); }
`;

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 6px 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--color-primary-muted) 45%, var(--color-bg-subtle) 55%);
  border: 1px solid var(--color-primary-muted);
  border-left: 3px solid var(--color-primary);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const counterStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-primary);
`;

const navStyle = css`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const navBtnStyle = css`
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1em;
  line-height: 1;
  cursor: pointer;
  padding: 3px 7px;
  border-radius: 6px;

  &:hover:not(:disabled) { background: var(--color-bg-raised); color: var(--color-text); }
  &:disabled { opacity: 0.35; cursor: default; }
`;

const bodyStyle = css`
  font-size: 0.92em;
  color: var(--color-text);
  line-height: 1.55;

  p { margin: 0.3em 0; }
  p:first-of-type { margin-top: 0; }
  p:last-of-type { margin-bottom: 0; }
  strong { font-weight: 600; }
  code {
    background: var(--color-bg-raised);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.92em;
  }
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 2px;
`;

const revealMoreStyle = css`
  border: none;
  background: transparent;
  color: var(--color-primary);
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;

  &:hover:not(:disabled) { text-decoration: underline; text-underline-offset: 2px; }
  &:disabled { color: var(--color-text-muted); cursor: not-allowed; }
`;

const cappedStyle = css`
  font-size: 0.78em;
  color: var(--color-text-muted);
`;

interface HintCardProps {
  hints: HintEntry[];
  /** True while the newest hint is still streaming. */
  streaming: boolean;
  /** Generate the next hint (only ever called from explicit reveal buttons). */
  onReveal: () => void;
  /** Active step id — resets local view state when the learner advances. */
  interactionId: string;
  maxHints?: number;
}

export const HintCard: React.FC<HintCardProps> = ({
  hints,
  streaming,
  onReveal,
  interactionId,
  maxHints = DEFAULT_MAX_HINTS,
}) => {
  const count = hints.length;
  const [collapsed, setCollapsed] = useState(false);
  const [viewIndex, setViewIndex] = useState(0);

  // Jump to the newest hint whenever one is added (and expand to show it).
  useEffect(() => {
    if (count > 0) {
      setViewIndex(count - 1);
      setCollapsed(false);
    }
  }, [count]);

  // Reset when the learner moves to a different step.
  useEffect(() => {
    setCollapsed(false);
    setViewIndex(0);
  }, [interactionId]);

  // Empty state: a quiet "Hint" affordance — but only once the interaction
  // has finished generating.  Showing it mid-stream pulls attention while
  // the content is still loading.
  if (count === 0) {
    if (streaming) return null;
    return (
      <button
        type="button"
        className={emptyButtonStyle}
        onClick={onReveal}
        data-testid="hint-reveal"
      >
        <span aria-hidden="true">💡</span>
        Hint
      </button>
    );
  }

  if (collapsed) {
    return (
      <button
        type="button"
        className={chipStyle}
        onClick={() => setCollapsed(false)}
        data-testid="hint-chip"
      >
        <span aria-hidden="true">💡</span>
        {count === 1 ? "1 hint" : `${count} hints`}
      </button>
    );
  }

  const idx = Math.min(viewIndex, count - 1);
  const current = hints[idx];
  const body = current?.message ?? "";
  const atCap = count >= maxHints;
  const isNewestStreaming = streaming && idx === count - 1 && !body;

  return (
    <div className={cardStyle} data-testid="hint-card">
      <div className={headerStyle}>
        <span className={counterStyle} data-testid="hint-card-counter">
          💡 Hint {idx + 1} of {count}
        </span>
        <div className={navStyle}>
          {count > 1 && (
            <>
              <button
                type="button"
                className={navBtnStyle}
                onClick={() => setViewIndex((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                aria-label="Previous hint"
                data-testid="hint-card-prev"
              >
                ‹
              </button>
              <button
                type="button"
                className={navBtnStyle}
                onClick={() => setViewIndex((i) => Math.min(count - 1, i + 1))}
                disabled={idx === count - 1}
                aria-label="Next hint"
                data-testid="hint-card-next"
              >
                ›
              </button>
            </>
          )}
          <button
            type="button"
            className={navBtnStyle}
            onClick={() => setCollapsed(true)}
            aria-label="Collapse hints"
            data-testid="hint-card-collapse"
          >
            ×
          </button>
        </div>
      </div>

      <div className={bodyStyle} data-testid="hint-card-body">
        {isNewestStreaming ? (
          <PendingIndicator variant="inline" label="Thinking of a nudge…" />
        ) : (
          <MathMarkdown>{body}</MathMarkdown>
        )}
      </div>

      <div className={footerStyle}>
        {atCap ? (
          <span className={cappedStyle}>That's all the hints for this one.</span>
        ) : (
          <button
            type="button"
            className={revealMoreStyle}
            onClick={onReveal}
            disabled={streaming}
            data-testid="hint-reveal"
          >
            Need more? Reveal another hint →
          </button>
        )}
      </div>
    </div>
  );
};
